// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCallback, useEffect, useRef, useState } from 'react';

// Ignores the last sliver, so the pill does not linger at the end of a list
const SCROLL_REMAINDER_PX = 16;

// Idle time that counts as the scroll having stopped; wider than a frame gap
const SCROLL_SETTLE_MS = 300;

function hasContentBelow(element: HTMLElement): boolean {
  const remaining =
    element.scrollHeight - element.clientHeight - element.scrollTop;

  return remaining > SCROLL_REMAINDER_PX;
}

/**
 * Tracks whether a scroll container has more content below the fold.
 *
 * The hook owns the element rather than taking a ref, because it has to know
 * the moment the node attaches: a container rendered behind a menu or a
 * transition mounts a commit later than the state that renders it, and a ref
 * read from an effect is still null at that point.
 *
 * Attach `ref` to the scroll container and read `element` for imperative work:
 *
 * ```tsx
 * const scrollIndicator = useScrollIndicator();
 * <div ref={scrollIndicator.ref}>…</div>
 * ```
 */
export function useScrollIndicator() {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // A dismissal lasts until scrolling stops, not until the end is reached
  const isSuppressed = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSettleTimer = useCallback(() => {
    if (settleTimer.current !== null) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
  }, []);

  const scheduleSettle = useCallback(() => {
    clearSettleTimer();

    settleTimer.current = setTimeout(() => {
      isSuppressed.current = false;
      setIsVisible(element !== null && hasContentBelow(element));
    }, SCROLL_SETTLE_MS);
  }, [clearSettleTimer, element]);

  useEffect(() => {
    if (element === null) {
      // The dismissal and the reading both describe a container that is gone
      isSuppressed.current = false;
      clearSettleTimer();
      setIsVisible(false);
      return;
    }

    const publish = () => {
      if (isSuppressed.current) {
        return;
      }

      setIsVisible(hasContentBelow(element));
    };

    const handleScroll = () => {
      if (isSuppressed.current) {
        scheduleSettle();
        return;
      }

      publish();
    };

    // Rows arriving resize nothing already observed, hence the child-list watch
    const resizeObserver = new ResizeObserver(publish);
    const observeContent = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(element);

      Array.from(element.children).forEach((child) => {
        resizeObserver.observe(child);
      });
    };

    const mutationObserver = new MutationObserver(() => {
      observeContent();
      publish();
    });

    observeContent();
    mutationObserver.observe(element, { childList: true });
    element.addEventListener('scroll', handleScroll);
    publish();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      element.removeEventListener('scroll', handleScroll);

      // Swapping the node never renders null, so the branch above never runs
      isSuppressed.current = false;
      clearSettleTimer();
    };
  }, [element, clearSettleTimer, scheduleSettle]);

  useEffect(() => clearSettleTimer, [clearSettleTimer]);

  const hide = useCallback(() => {
    isSuppressed.current = true;
    setIsVisible(false);
    // An element already at the end fires no scroll, so give this its own end
    scheduleSettle();
  }, [scheduleSettle]);

  return { ref: setElement, element, isVisible, hide };
}
